import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { 
  TrendingUp, 
  Users, 
  MapPin, 
  Clock,
  Eye,
  Smartphone,
  BarChart3,
  Target,
  QrCode,
  Heart,
  Share2,
  Phone,
  Activity,
  PieChart,
  Sun,
  Moon,
  RefreshCw,
  CheckCircle,
  AlertCircle,
  Wifi,
  WifiOff
} from 'lucide-react';

export default function Analytics() {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState<'campaigns' | 'qr' | 'engagement'>('campaigns');
  const [theme, setTheme] = useState<'light' | 'dark'>('light');
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  // Campaign Analytics State
  const [campaignAnalytics, setCampaignAnalytics] = useState<any>(null);
  const [timeRange, setTimeRange] = useState<'24h' | '7d' | '30d'>('7d');
  const [campaignWebhookStatus, setCampaignWebhookStatus] = useState<'loading' | 'success' | 'error'>('loading');
  
  // QR Analytics State  
  const [qrAnalytics, setQrAnalytics] = useState<any>(null);
  const [qrWebhookStatus, setQrWebhookStatus] = useState<'loading' | 'success' | 'error'>('loading');

  // Visitor Engagement State
  const [engagementMetrics, setEngagementMetrics] = useState<any>(null);
  const [engagementWebhookStatus, setEngagementWebhookStatus] = useState<'loading' | 'success' | 'error'>('loading');

  // 🔒 Calculate Engagement Rate Function - COMPLETELY REWRITTEN
  const calculateEngagementRate = (metrics: any) => {
    const totalClaims = metrics?.totalClaims || 0;
    const totalScans = metrics?.totalScans || 0;
    const totalEngagements = metrics?.totalEngagements || 0;
    const uniqueVisitors = metrics?.uniqueVisitors || 0;
    const activeVisitors = metrics?.activeVisitors || 0;
    
    let calculatedRate = 0;
    
    // PRIMARY: Use claims rate (claims ÷ scans) if both exist
    if (totalScans > 0 && totalClaims > 0) {
      calculatedRate = Math.round((totalClaims / totalScans) * 100);
      console.log('📊 PRIMARY formula (claims/scans):', { totalClaims, totalScans, calculatedRate });
    } 
    // SECONDARY: Use engagement rate (engagements ÷ scans)
    else if (totalScans > 0 && totalEngagements > 0) {
      calculatedRate = Math.round((totalEngagements / totalScans) * 100);
      console.log('📊 SECONDARY formula (engagements/scans):', { totalEngagements, totalScans, calculatedRate });
    }
    // TERTIARY: Use visitor engagement ratio
    else if (uniqueVisitors > 0 && activeVisitors > 0) {
      calculatedRate = Math.round((activeVisitors / uniqueVisitors) * 100);
      console.log('📊 TERTIARY formula (active/total visitors):', { activeVisitors, uniqueVisitors, calculatedRate });
    }
    // QUATERNARY: Use simple engagement count as percentage (cap at 99%)
    else if (totalEngagements > 0) {
      // If we have engagements but no baseline, use a default baseline for realistic estimation
      const estimatedBaseline = Math.max(uniqueVisitors * 2, 10);
      calculatedRate = Math.round(Math.min((totalEngagements / estimatedBaseline) * 100, 99));
      console.log('📊 QUATERNARY formula (engagements/estimated baseline):', { totalEngagements, estimatedBaseline, calculatedRate });
    }
    
    console.log('📊 Final Engagement Rate:', { totalClaims, totalScans, totalEngagements, uniqueVisitors, activeVisitors, calculatedRate });
    
    return Math.min(calculatedRate, 100);
  };

  // 🔒 Calculate Active Visitors for last 2 hours - ENHANCED
  const calculateActiveVisitors = (metrics: any) => {
    // Try multiple data sources for visitor check-ins
    const checkinSources = [
      metrics?.visitorCheckins,
      metrics?.recentCheckins,
      metrics?.recentEngagements,
      metrics?.engagements,
      metrics?.checkins
    ];
    
    let checkins: any[] = [];
    for (const source of checkinSources) {
      if (source && Array.isArray(source) && source.length > 0) {
        checkins = source;
        console.log('📊 Found check-ins from source:', Object.keys(metrics).find(key => metrics[key] === source));
        break;
      }
    }
    
    if (checkins.length === 0) {
      console.log('📊 No check-in data found, returning 0 active visitors');
      return 0;
    }
    
    const now = new Date();
    const twoHoursAgo = new Date(now.getTime() - (2 * 60 * 60 * 1000));
    
    const recentCheckins = checkins.filter((checkin: any) => {
      const timestamp = checkin.created_at || checkin.engagement_timestamp || checkin.timestamp || checkin.checkin_time;
      if (!timestamp) return false;
      const checkinTime = new Date(timestamp);
      return !isNaN(checkinTime.getTime()) && checkinTime >= twoHoursAgo;
    });
    
    // Get unique visitors from recent check-ins using multiple identifiers
    const uniqueRecentVisitors = new Set(recentCheckins.map((checkin: any) => 
      checkin.visitor_phone || checkin.visitor_id || checkin.session_id || checkin.id || JSON.stringify(checkin)
    ));
    
    console.log('📊 Active Visitors Calculation:', {
      totalCheckins: checkins.length,
      recentCheckins: recentCheckins.length,
      uniqueRecentVisitors: uniqueRecentVisitors.size,
      timeframe: 'Last 2 hours',
      sourcesChecked: checkinSources.map(s => s ? 'found' : 'empty')
    });
    
    return uniqueRecentVisitors.size || recentCheckins.length; // Fallback to recent count if no unique identifiers
  };

  // 🔒 Generate Visitor Segmentation from Available Data
  const generateVisitorSegmentation = (metrics: any) => {
    // If backend sends segmentation data, use it
    if (metrics?.visitorSegmentation && metrics.visitorSegmentation.length > 0) {
      return metrics.visitorSegmentation;
    }
    
    // Otherwise, generate from engagement methods
    const methods = metrics?.topEngagementMethods || metrics?.engagementMethods || [];
    const totalEngagements = metrics?.totalEngagements || 0;
    
    if (methods.length === 0 && totalEngagements === 0) {
      return [];
    }
    
    // Generate segmentation from channel data
    const segmentation = methods.map((method: any) => ({
      type: method.method || method.channel || 'Unknown',
      count: method.count || 0,
      percentage: totalEngagements > 0 ? Math.round((method.count / totalEngagements) * 100) : 0
    }));
    
    // If no methods but have engagements, create basic segmentation
    if (segmentation.length === 0 && totalEngagements > 0) {
      segmentation.push({
        type: 'All Engagements',
        count: totalEngagements,
        percentage: 100
      });
    }
    
    console.log('📊 Generated segmentation:', segmentation);
    return segmentation;
  };

  // 🔒 Calculate Channel Totals from Available Data
  const calculateChannelTotals = (metrics: any) => {
    // If backend sends channel totals, use it
    if (metrics?.channelTotals && Object.keys(metrics.channelTotals).length > 0) {
      return metrics.channelTotals;
    }
    
    // Otherwise, calculate from engagement methods
    const methods = metrics?.topEngagementMethods || metrics?.engagementMethods || [];
    const totalEngagements = metrics?.totalEngagements || 0;
    
    if (methods.length === 0 && totalEngagements === 0) {
      return {};
    }
    
    // Calculate channel totals from method data
    const channelTotals: Record<string, number> = {};
    methods.forEach((method: any) => {
      const channelName = method.method || method.channel || 'Unknown';
      channelTotals[channelName] = (channelTotals[channelName] || 0) + (method.count || 0);
    });
    
    console.log('📊 Calculated channel totals:', channelTotals);
    return channelTotals;
  };

  // Auto-refresh interval for QR analytics
  useEffect(() => {
    if (activeTab === 'qr' && user) {
      fetchQRAnalytics();
      const interval = setInterval(fetchQRAnalytics, 30000); // Refresh every 30 seconds
      return () => clearInterval(interval);
    }
  }, [activeTab, user]);

  // Fetch Campaign Analytics
  useEffect(() => {
    if (activeTab === 'campaigns' && user) {
      fetchCampaignAnalytics();
    }
  }, [timeRange, activeTab, user]);

  // Fetch Visitor Engagement Analytics
  useEffect(() => {
    if (activeTab === 'engagement' && user) {
      fetchVisitorEngagement();
    }
  }, [activeTab, user]);

  const fetchCampaignAnalytics = async () => {
    setLoading(true);
    setCampaignWebhookStatus('loading');
    try {
      console.log('🔍 Fetching Campaign Analytics via webhook...');
      
      // Use unified webhook endpoint with analyticsType parameter
      const response = await fetch(
        `https://n8n.tenear.com/webhook/visitor-engagement-analytics?shop_id=${user?.shop_id}&mall_id=${user?.mall_id}&analytics_type=campaign`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            userId: user?.id,
            userType: user?.role,
            shopId: user?.shop_id,
            mallId: user?.mall_id,
            analyticsType: 'campaign'
          })
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      
      if (data.success) {
        setCampaignAnalytics(data);
        setCampaignWebhookStatus('success');
        console.log('✅ Campaign Analytics webhook successful:', {
          totalCampaigns: data.totalCampaigns,
          totalScans: data.totalScans,
          totalClaims: data.totalClaims
        });
      } else {
        throw new Error(data.error || 'Unknown webhook error');
      }

    } catch (error) {
      console.error('❌ Campaign Analytics webhook failed:', error);
      setCampaignWebhookStatus('error');
      // Set error state instead of mock data
      setCampaignAnalytics({
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error occurred',
        totalCampaigns: 0,
        activeCampaigns: 0,
        totalScans: 0,
        totalClaims: 0,
        avgEngagement: 0
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchQRAnalytics = async () => {
    setRefreshing(true);
    setQrWebhookStatus('loading');
    try {
      console.log('🔍 Fetching QR Analytics via webhook...');
      
      // Use unified webhook endpoint with analyticsType parameter
      const response = await fetch(
        `https://n8n.tenear.com/webhook/visitor-engagement-analytics?shop_id=${user?.shop_id}&mall_id=${user?.mall_id}&analytics_type=qr`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            userId: user?.id,
            userType: user?.role,
            shopId: user?.shop_id,
            mallId: user?.mall_id,
            analyticsType: 'qr'
          })
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      
      if (data.success) {
        setQrAnalytics(data);
        setQrWebhookStatus('success');
        console.log('✅ QR Analytics webhook successful:', {
          totalCheckins: data.totalCheckins,
          todayCheckins: data.todayCheckins,
          peakZone: data.peakZone
        });
      } else {
        throw new Error(data.error || 'Unknown webhook error');
      }

    } catch (error) {
      console.error('❌ QR Analytics webhook failed:', error);
      setQrWebhookStatus('error');
      // Set error state instead of mock data
      setQrAnalytics({
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error occurred',
        totalCheckins: 0,
        peakZone: 'Error loading',
        dailyCheckins: 0,
        zonePerformance: [],
        hourlyData: [],
        recentActivity: []
      });
    } finally {
      setRefreshing(false);
    }
  };

  const fetchVisitorEngagement = async () => {
    setEngagementWebhookStatus('loading');
    try {
      console.log('🔍 Fetching Visitor Engagement via webhook...');
      
      // Include time range and mall_id for more accurate data
      // Use unified webhook endpoint - the visitor-engagement-analytics handles all types
      const response = await fetch(
        `https://n8n.tenear.com/webhook/visitor-engagement-analytics?shop_id=${user?.shop_id}&mall_id=${user?.mall_id}&analytics_type=engagement`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            userId: user?.id,
            userType: user?.role,
            shopId: user?.shop_id || 0,
            mallId: user?.mall_id || 0,
            timeRange: '24h', // Get data for last 24 hours for better active visitor calculation
            includeCheckins: true, // Request raw check-in data for accurate calculations
            analyticsType: 'engagement'
          })
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      
      if (data.success) {
        // Apply data accuracy fixes
        const enhancedData = {
          ...data,
          // Fix active visitors calculation if needed
          activeVisitors: data.activeVisitors || calculateActiveVisitors(data),
          // Fix channel totals if needed
          channelTotals: calculateChannelTotals(data),
          // Add timestamp for debugging
          lastUpdated: new Date().toISOString()
        };
        
        setEngagementMetrics(enhancedData);
        setEngagementWebhookStatus('success');
        console.log('✅ Visitor Engagement webhook successful - DATA ACCURACY ENHANCED:', {
          totalEngagements: enhancedData.totalEngagements,
          activeVisitors: enhancedData.activeVisitors,
          engagementRate: calculateEngagementRate(enhancedData),
          dataAccuracy: 'Enhanced with calculations',
          timestamp: enhancedData.lastUpdated
        });
      } else {
        throw new Error(data.error || 'Unknown webhook error');
      }

    } catch (error) {
      console.error('❌ Visitor Engagement webhook failed:', error);
      setEngagementWebhookStatus('error');
      // Set error state instead of mock data
      setEngagementMetrics({
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error occurred',
        totalEngagements: 0,
        activeVisitors: 0,
        engagementRate: 0,
        avgEngagementTime: 0,
        topEngagementMethods: [],
        visitorSegmentation: [],
        recentEngagements: []
      });
    }
  };

  const formatTime = (timestamp: string) => {
    return new Date(timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const getWebhookStatusIcon = (status: 'loading' | 'success' | 'error') => {
    switch (status) {
      case 'loading':
        return <RefreshCw className="w-4 h-4 animate-spin text-blue-500" />;
      case 'success':
        return <CheckCircle className="w-4 h-4 text-green-500" />;
      case 'error':
        return <AlertCircle className="w-4 h-4 text-red-500" />;
    }
  };

  const getWebhookStatusText = (status: 'loading' | 'success' | 'error') => {
    switch (status) {
      case 'loading':
        return 'Loading...';
      case 'success':
        return 'Connected';
      case 'error':
        return 'Failed';
    }
  };

  // 🔄 Data refresh function for manual updates
  const refreshData = async () => {
    console.log('🔄 Refreshing analytics data for active tab:', activeTab);
    switch (activeTab) {
      case 'campaigns':
        await fetchCampaignAnalytics();
        break;
      case 'qr':
        await fetchQRAnalytics();
        break;
      case 'engagement':
        await fetchVisitorEngagement();
        break;
      default:
        console.warn('🔄 Unknown active tab for refresh:', activeTab);
    }
  };

  // 🔒 SECURITY: Debug authentication state (don't block execution)
  if (user) {
    console.log('🔒 Analytics: User authenticated for shop analytics:', {
      userId: user.id,
      shopId: user.shop_id,
      mallId: user.mall_id,
      role: user.role
    });
  }

  // Early return for unauthenticated users - this was blocking execution
  if (!user) {
    console.log('⚠️ Analytics: User not authenticated');
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <AlertCircle className="mx-auto h-8 w-8 text-red-500 mb-2" />
          <p className="text-gray-600">Authentication required</p>
          <p className="text-sm text-gray-500">Please log in to access analytics</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`space-y-6 ${theme === 'dark' ? 'dark' : ''}`}>
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Analytics Dashboard</h1>
          <p className="text-gray-600 mt-1">
            Real-time analytics via N8N webhooks • Multi-platform engagement tracking
            <Badge variant="secondary" className="ml-2 bg-gradient-to-r from-purple-100 to-blue-100 text-purple-700 border-purple-200">
              🌐 All Platforms
            </Badge>
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            onClick={() => setTheme(theme === 'light' ? 'dark' : 'light')}
            variant="outline"
            size="sm"
          >
            {theme === 'light' ? <Moon className="w-4 h-4" /> : <Sun className="w-4 h-4" />}
          </Button>
        </div>
      </div>

      {/* Webhook Status Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="border-2">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                {getWebhookStatusIcon(campaignWebhookStatus)}
                <span className="font-medium">Campaign Analytics</span>
              </div>
              <Badge variant={campaignWebhookStatus === 'success' ? 'default' : 'secondary'}>
                {getWebhookStatusText(campaignWebhookStatus)}
              </Badge>
            </div>
          </CardContent>
        </Card>

        <Card className="border-2">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                {getWebhookStatusIcon(qrWebhookStatus)}
                <span className="font-medium">QR Analytics</span>
              </div>
              <Badge variant={qrWebhookStatus === 'success' ? 'default' : 'secondary'}>
                {getWebhookStatusText(qrWebhookStatus)}
              </Badge>
            </div>
          </CardContent>
        </Card>

        <Card className="border-2">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                {getWebhookStatusIcon(engagementWebhookStatus)}
                <span className="font-medium">Visitor Engagement</span>
              </div>
              <Badge variant={engagementWebhookStatus === 'success' ? 'default' : 'secondary'}>
                {getWebhookStatusText(engagementWebhookStatus)}
              </Badge>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tab Navigation */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab('campaigns')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'campaigns'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <BarChart3 className="w-4 h-4 inline mr-2" />
            Campaign Analytics
          </button>
          <button
            onClick={() => setActiveTab('qr')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'qr'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <QrCode className="w-4 h-4 inline mr-2" />
            QR Analytics
            <Badge variant="secondary" className="ml-2 bg-green-100 text-green-800">
              Live
            </Badge>
          </button>
          <button
            onClick={() => setActiveTab('engagement')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'engagement'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <Target className="w-4 h-4 inline mr-2" />
            Visitor Engagement
            <Badge variant="secondary" className="ml-2 bg-purple-100 text-purple-800">
              Smart
            </Badge>
          </button>
        </nav>
      </div>

      {/* Campaign Analytics Tab */}
      {activeTab === 'campaigns' && (
        <CampaignAnalytics 
          analytics={campaignAnalytics}
          loading={loading}
          timeRange={timeRange}
          setTimeRange={setTimeRange}
          fetchCampaignAnalytics={fetchCampaignAnalytics}
          formatTime={formatTime}
          webhookStatus={campaignWebhookStatus}
        />
      )}

      {/* QR Analytics Tab */}
      {activeTab === 'qr' && (
        <QRAnalytics 
          qrAnalytics={qrAnalytics}
          refreshing={refreshing}
          fetchQRAnalytics={fetchQRAnalytics}
          webhookStatus={qrWebhookStatus}
        />
      )}

      {/* Visitor Engagement Tab */}
      {activeTab === 'engagement' && (
        <VisitorEngagementAnalytics 
          metrics={engagementMetrics}
          loading={engagementWebhookStatus === 'loading'}
          refreshData={fetchVisitorEngagement}
          webhookStatus={engagementWebhookStatus}
          formatTime={formatTime}
          calculateEngagementRate={calculateEngagementRate}
        />
      )}
    </div>
  );
}

// Campaign Analytics Component
function CampaignAnalytics({ 
  analytics, 
  loading, 
  timeRange, 
  setTimeRange, 
  fetchCampaignAnalytics,
  formatTime,
  webhookStatus 
}: any) {
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (!analytics || !analytics.success) {
    return (
      <div className="space-y-6">
        <div className="text-center py-8 text-gray-500">
          <AlertCircle className="mx-auto h-8 w-8 mb-2" />
          <p>Campaign Analytics Unavailable</p>
          <p className="text-sm">
            {analytics?.error || 'No campaign data available'}
          </p>
          <Button onClick={fetchCampaignAnalytics} variant="outline" size="sm" className="mt-4">
            <RefreshCw className="w-4 h-4 mr-2" />
            Retry Connection
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Time Range Controls */}
      <div className="flex gap-2">
        {(['24h', '7d', '30d'] as const).map((range) => (
          <Button
            key={range}
            onClick={() => setTimeRange(range)}
            variant={timeRange === range ? "default" : "outline"}
            size="sm"
          >
            {range}
          </Button>
        ))}
        <Button
          onClick={() => fetchCampaignAnalytics()}
          variant="outline"
          size="sm"
        >
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Overview Cards */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Total Campaigns</p>
                <p className="text-2xl font-bold text-blue-600">{analytics.totalCampaigns || 0}</p>
              </div>
              <Target className="h-8 w-8 text-blue-500" />
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Active Campaigns</p>
                <p className="text-2xl font-bold text-green-600">{analytics.activeCampaigns || 0}</p>
              </div>
              <Eye className="h-8 w-8 text-green-500" />
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Total Scans</p>
                <p className="text-2xl font-bold text-purple-600">{analytics.totalScans || 0}</p>
              </div>
              <QrCode className="h-8 w-8 text-purple-500" />
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Total Claims</p>
                <p className="text-2xl font-bold text-orange-600">{analytics.totalClaims || 0}</p>
              </div>
              <Target className="h-8 w-8 text-orange-500" />
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Avg Engagement</p>
                <p className="text-2xl font-bold text-indigo-600">
                  {Math.min(analytics.avgEngagement || 0, 100)}%
                </p>
              </div>
              <TrendingUp className="h-8 w-8 text-indigo-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Multi-Platform Campaign Performance */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <span>🎯</span>
            <span>Multi-Platform Campaign Performance</span>
            <Badge variant="secondary" className="bg-gradient-to-r from-blue-100 to-purple-100 text-blue-700 border-blue-200">
              Live Data
            </Badge>
          </CardTitle>
          <CardDescription>Campaign performance across all engagement platforms - Real backend data</CardDescription>
        </CardHeader>
        <CardContent>
          {analytics.campaignPerformance && analytics.campaignPerformance.length > 0 ? (
            <div className="space-y-4">
              {analytics.campaignPerformance.map((campaign: any, index: number) => {
                // Enhanced platform detection for campaign names
                const getPlatformConfig = (campaignName: string, campaignType: string) => {
                  const name = (campaignName + ' ' + campaignType).toLowerCase();
                  if (name.includes('whatsapp') || name.includes('wa') || name.includes('whats')) {
                    return { 
                      icon: '📱', 
                      color: 'bg-green-100 border-green-200', 
                      iconBg: 'bg-green-500',
                      textColor: 'text-green-700',
                      name: 'WhatsApp',
                      badgeColor: 'bg-green-50 text-green-700 border-green-200'
                    };
                  } else if (name.includes('qr') || name.includes('scan') || name.includes('qr-code')) {
                    return { 
                      icon: '🔍', 
                      color: 'bg-blue-100 border-blue-200', 
                      iconBg: 'bg-blue-500',
                      textColor: 'text-blue-700',
                      name: 'QR Code',
                      badgeColor: 'bg-blue-50 text-blue-700 border-blue-200'
                    };
                  } else if (name.includes('instagram') || name.includes('ig') || name.includes('insta')) {
                    return { 
                      icon: '📸', 
                      color: 'bg-pink-100 border-pink-200', 
                      iconBg: 'bg-pink-500',
                      textColor: 'text-pink-700',
                      name: 'Instagram',
                      badgeColor: 'bg-pink-50 text-pink-700 border-pink-200'
                    };
                  } else if (name.includes('facebook') || name.includes('fb') || name.includes('messenger')) {
                    return { 
                      icon: '👍', 
                      color: 'bg-blue-100 border-blue-200', 
                      iconBg: 'bg-blue-600',
                      textColor: 'text-blue-700',
                      name: 'Facebook',
                      badgeColor: 'bg-blue-50 text-blue-700 border-blue-200'
                    };
                  } else if (name.includes('telegram') || name.includes('tele')) {
                    return { 
                      icon: '✈️', 
                      color: 'bg-cyan-100 border-cyan-200', 
                      iconBg: 'bg-cyan-500',
                      textColor: 'text-cyan-700',
                      name: 'Telegram',
                      badgeColor: 'bg-cyan-50 text-cyan-700 border-cyan-200'
                    };
                  } else if (name.includes('tiktok') || name.includes('tik')) {
                    return { 
                      icon: '🎵', 
                      color: 'bg-black bg-opacity-10 border-gray-200', 
                      iconBg: 'bg-black',
                      textColor: 'text-gray-700',
                      name: 'TikTok',
                      badgeColor: 'bg-gray-50 text-gray-700 border-gray-200'
                    };
                  } else {
                    return { 
                      icon: '🌐', 
                      color: 'bg-gray-100 border-gray-200', 
                      iconBg: 'bg-gray-500',
                      textColor: 'text-gray-700',
                      name: 'Multi-Platform',
                      badgeColor: 'bg-gray-50 text-gray-700 border-gray-200'
                    };
                  }
                };

                const platform = getPlatformConfig(campaign.name, campaign.type);
                
                return (
                  <div key={index} className={`border-2 rounded-lg p-4 ${platform.color}`}>
                    <div className="flex justify-between items-start mb-4">
                      <div className="flex items-center space-x-3">
                        <div className={`w-12 h-12 ${platform.iconBg} rounded-full flex items-center justify-center text-white text-xl`}>
                          {platform.icon}
                        </div>
                        <div>
                          <h4 className="font-bold text-gray-900 text-lg">{campaign.name}</h4>
                          <div className="flex items-center space-x-2 mt-1">
                            <Badge variant="outline" className={platform.badgeColor}>
                              {platform.name}
                            </Badge>
                            <span className="text-sm text-gray-500">•</span>
                            <span className="text-sm text-gray-600">{campaign.type}</span>
                          </div>
                        </div>
                      </div>
                      <div className="text-right">
                        <Badge variant={Math.min(campaign.engagementRate || 0, 100) > 70 ? 'default' : 'secondary'} className="mb-1">
                          {Math.min(campaign.engagementRate || 0, 100)}% engagement
                        </Badge>
                        {campaign.engagementRate > 100 && (
                          <div className="text-xs text-amber-600">⚠️ Capped at 100%</div>
                        )}
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-3 gap-4 text-sm">
                      <div className="text-center p-3 bg-white bg-opacity-50 rounded-lg">
                        <div className="text-gray-500 text-xs uppercase tracking-wide">Total Scans</div>
                        <div className="font-bold text-lg text-blue-600">{campaign.scans || 0}</div>
                      </div>
                      <div className="text-center p-3 bg-white bg-opacity-50 rounded-lg">
                        <div className="text-gray-500 text-xs uppercase tracking-wide">Claims/Inquiries</div>
                        <div className="font-bold text-lg text-green-600">{campaign.claims || 0}</div>
                      </div>
                      <div className="text-center p-3 bg-white bg-opacity-50 rounded-lg">
                        <div className="text-gray-500 text-xs uppercase tracking-wide">Conversion Rate</div>
                        <div className="font-bold text-lg text-purple-600">
                          {campaign.scans > 0 ? Math.min(Math.round(((campaign.claims || 0) / campaign.scans) * 100), 100) : 0}%
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              <BarChart3 className="mx-auto h-12 w-12 mb-4 text-gray-400" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">Multi-Platform Campaign Data</h3>
              <p className="text-gray-600 mb-4">Your campaigns across all platforms will appear here:</p>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3 max-w-lg mx-auto">
                <div className="flex items-center space-x-2 bg-green-50 p-2 rounded-lg">
                  <span className="text-lg">📱</span>
                  <span className="text-sm font-medium text-green-700">WhatsApp</span>
                </div>
                <div className="flex items-center space-x-2 bg-blue-50 p-2 rounded-lg">
                  <span className="text-lg">🔍</span>
                  <span className="text-sm font-medium text-blue-700">QR Code</span>
                </div>
                <div className="flex items-center space-x-2 bg-pink-50 p-2 rounded-lg">
                  <span className="text-lg">📸</span>
                  <span className="text-sm font-medium text-pink-700">Instagram</span>
                </div>
                <div className="flex items-center space-x-2 bg-blue-50 p-2 rounded-lg">
                  <span className="text-lg">👍</span>
                  <span className="text-sm font-medium text-blue-700">Facebook</span>
                </div>
                <div className="flex items-center space-x-2 bg-cyan-50 p-2 rounded-lg">
                  <span className="text-lg">✈️</span>
                  <span className="text-sm font-medium text-cyan-700">Telegram</span>
                </div>
                <div className="flex items-center space-x-2 bg-gray-50 p-2 rounded-lg">
                  <span className="text-lg">🎵</span>
                  <span className="text-sm font-medium text-gray-700">TikTok</span>
                </div>
              </div>
              <p className="text-sm text-gray-500 mt-4">Real-time data from your N8N webhook will populate this section</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// QR Analytics Component
function QRAnalytics({ qrAnalytics, refreshing, fetchQRAnalytics, webhookStatus }: any) {
  if (!qrAnalytics || !qrAnalytics.success) {
    return (
      <div className="space-y-6">
        <div className="flex gap-2">
          <Button
            onClick={() => fetchQRAnalytics()}
            disabled={refreshing}
            variant="outline"
            size="sm"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            {refreshing ? 'Refreshing...' : 'Retry Connection'}
          </Button>
          {qrAnalytics?.error && (
            <Badge variant="destructive">
              Error: {qrAnalytics.error}
            </Badge>
          )}
        </div>

        <div className="text-center py-8 text-gray-500">
          <QrCode className="mx-auto h-8 w-8 mb-2" />
          <p>QR Analytics Unavailable</p>
          <p className="text-sm">
            {qrAnalytics?.error || 'No QR analytics data available'}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* QR Header Controls */}
      <div className="flex gap-2">
        <Button
          onClick={() => fetchQRAnalytics()}
          disabled={refreshing}
          variant="outline"
          size="sm"
        >
          <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
          {refreshing ? 'Refreshing...' : 'Refresh Data'}
        </Button>
        <Badge variant="secondary" className="bg-green-100 text-green-800">
          Auto-refresh: 30s
        </Badge>
      </div>

      {/* Real-time KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Total Check-ins</p>
                <p className="text-2xl font-bold text-blue-600">{qrAnalytics.totalCheckins || 0}</p>
                <p className="text-xs text-gray-500 mt-1">All time</p>
              </div>
              <Users className="h-8 w-8 text-blue-500" />
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Today's Check-ins</p>
                <p className="text-2xl font-bold text-green-600">{qrAnalytics.todayCheckins || 0}</p>
                <p className="text-xs text-gray-500 mt-1">Live data</p>
              </div>
              <Activity className="h-8 w-8 text-green-500" />
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Peak Zone</p>
                <p className="text-2xl font-bold text-purple-600">{qrAnalytics.peakZone || 'N/A'}</p>
                <p className="text-xs text-gray-500 mt-1">Most active</p>
              </div>
              <MapPin className="h-8 w-8 text-purple-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Zone Performance */}
      <Card>
        <CardHeader>
          <CardTitle>Zone Performance</CardTitle>
          <CardDescription>Check-in distribution across mall zones - Real data</CardDescription>
        </CardHeader>
        <CardContent>
          {qrAnalytics.zonePerformance && qrAnalytics.zonePerformance.length > 0 ? (
            <div className="space-y-3">
              {qrAnalytics.zonePerformance.map((zone: any, index: number) => (
                <div key={index} className="flex items-center justify-between p-3 border rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center text-blue-600 font-bold text-sm">
                      {zone.zone.charAt(0).toUpperCase()}
                    </div>
                    <div>
                      <div className="font-medium">{zone.zone}</div>
                      <div className="text-sm text-gray-500">{zone.checkins} check-ins</div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="font-semibold text-blue-600">{Math.min(zone.percentage, 100)}%</div>
                    <div className="w-16 h-2 bg-gray-200 rounded mt-1">
                      <div 
                        className="h-2 bg-blue-500 rounded" 
                        style={{ width: `${Math.min(zone.percentage, 100)}%` }}
                      ></div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              <QrCode className="mx-auto h-8 w-8 mb-2" />
              <p>No QR check-in data available</p>
              <p className="text-sm">QR analytics will appear here when customers scan codes</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// Visitor Engagement Analytics Component
function VisitorEngagementAnalytics({ metrics, loading, refreshData, webhookStatus, formatTime, calculateEngagementRate }: any) {
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-500"></div>
      </div>
    );
  }

  if (!metrics || !metrics.success) {
    return (
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <h2 className="text-2xl font-bold text-gray-900">Visitor Engagement</h2>
          <Button onClick={refreshData} variant="outline" size="sm">
            <RefreshCw className="w-4 h-4 mr-2" />
            Retry Connection
          </Button>
        </div>

        <div className="text-center py-8 text-gray-500">
          <Target className="mx-auto h-8 w-8 mb-2" />
          <p>Visitor Engagement Unavailable</p>
          <p className="text-sm">
            {metrics?.error || 'No visitor engagement data available'}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header with Refresh */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Visitor Engagement</h2>
          <p className="text-gray-600">Smart visitor engagement strategy & analytics - Real data</p>
        </div>
        <Button onClick={refreshData} variant="outline" size="sm">
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Multi-Platform Overview Summary */}
      <Card className="bg-gradient-to-r from-purple-50 to-blue-50 border-2 border-purple-200">
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <span>🌐</span>
            <span>Multi-Platform Engagement Summary</span>
            <Badge variant="secondary" className="bg-purple-100 text-purple-700 border-purple-200">
              Real-time Data
            </Badge>
          </CardTitle>
          <CardDescription>Combined analytics across all your engagement platforms</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {/* Active Platforms Count */}
            <div className="text-center">
              <div className="text-3xl font-bold text-purple-600 mb-1">
                {metrics.topEngagementMethods?.length || 0}
              </div>
              <div className="text-sm text-gray-600">Active Platforms</div>
              <div className="text-xs text-gray-500 mt-1">
                {metrics.topEngagementMethods?.length > 0 ? 'Multi-channel active' : 'Single platform'}
              </div>
            </div>
            
            {/* Total Engagements */}
            <div className="text-center">
              <div className="text-3xl font-bold text-blue-600 mb-1">
                {metrics.totalEngagements || 0}
              </div>
              <div className="text-sm text-gray-600">Total Interactions</div>
              <div className="text-xs text-gray-500 mt-1">Across all channels</div>
            </div>
            
            {/* Engagement Rate */}
            <div className="text-center">
              <div className="text-3xl font-bold text-green-600 mb-1">
                {calculateEngagementRate(metrics)}%
              </div>
              <div className="text-sm text-gray-600">Avg. Engagement</div>
              <div className="text-xs text-gray-500 mt-1">Conversion rate</div>
            </div>
            
            {/* Top Platform */}
            <div className="text-center">
              <div className="text-3xl font-bold text-orange-600 mb-1">
                {(() => {
                  if (!metrics.topEngagementMethods || metrics.topEngagementMethods.length === 0) return 'N/A';
                  const topMethod = metrics.topEngagementMethods[0];
                  if (topMethod.method.toLowerCase().includes('whatsapp')) return '📱';
                  if (topMethod.method.toLowerCase().includes('qr') || topMethod.method.toLowerCase().includes('scan')) return '🔍';
                  if (topMethod.method.toLowerCase().includes('instagram')) return '📸';
                  if (topMethod.method.toLowerCase().includes('facebook')) return '👍';
                  if (topMethod.method.toLowerCase().includes('telegram')) return '✈️';
                  if (topMethod.method.toLowerCase().includes('tiktok')) return '🎵';
                  return '🌐';
                })()}
              </div>
              <div className="text-sm text-gray-600">Top Platform</div>
              <div className="text-xs text-gray-500 mt-1">
                {(() => {
                  if (!metrics.topEngagementMethods || metrics.topEngagementMethods.length === 0) return 'No data';
                  const topMethod = metrics.topEngagementMethods[0];
                  if (topMethod.method.toLowerCase().includes('whatsapp')) return 'WhatsApp';
                  if (topMethod.method.toLowerCase().includes('qr') || topMethod.method.toLowerCase().includes('scan')) return 'QR Code';
                  if (topMethod.method.toLowerCase().includes('instagram')) return 'Instagram';
                  if (topMethod.method.toLowerCase().includes('facebook')) return 'Facebook';
                  if (topMethod.method.toLowerCase().includes('telegram')) return 'Telegram';
                  if (topMethod.method.toLowerCase().includes('tiktok')) return 'TikTok';
                  return 'Multi-Platform';
                })()}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Engagement KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Total Engagements</p>
                <p className="text-2xl font-bold text-purple-600">{metrics.totalEngagements || 0}</p>
                <p className="text-xs text-gray-500 mt-1">
                  {metrics.topEngagementMethods && metrics.topEngagementMethods.length > 1 
                    ? `${metrics.topEngagementMethods.length} channels active` 
                    : 'Primary channel only'
                  }
                </p>
              </div>
              <Target className="h-8 w-8 text-purple-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Active Visitors</p>
                <p className="text-2xl font-bold text-green-600">{metrics.activeVisitors || 0}</p>
                <p className="text-xs text-gray-500 mt-1">Last 2 hours</p>
              </div>
              <Activity className="h-8 w-8 text-green-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Engagement Rate</p>
                <p className="text-2xl font-bold text-blue-600">
                  {calculateEngagementRate(metrics)}%
                </p>
                <p className="text-xs text-gray-500 mt-1">Visitors engaged</p>
              </div>
              <TrendingUp className="h-8 w-8 text-blue-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Avg. Engagement</p>
                <p className="text-2xl font-bold text-orange-600">{metrics.avgEngagementTime || 0}m</p>
                <p className="text-xs text-gray-500 mt-1">Duration</p>
              </div>
              <Clock className="h-8 w-8 text-orange-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Multi-Platform Engagement Methods */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <span>🌐</span>
              <span>Top Engagement Methods</span>
            </CardTitle>
            <CardDescription>All platforms and communication channels - Real data</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {metrics.topEngagementMethods && metrics.topEngagementMethods.length > 0 ? (
                metrics.topEngagementMethods.map((method: any, index: number) => {
                  // Platform-specific styling and icons
                  const getPlatformConfig = (methodName: string) => {
                    const name = methodName.toLowerCase();
                    if (name.includes('whatsapp') || name.includes('wa')) {
                      return { 
                        icon: '📱', 
                        color: 'bg-green-100', 
                        textColor: 'text-green-600', 
                        barColor: 'bg-green-500',
                        name: 'WhatsApp'
                      };
                    } else if (name.includes('qr') || name.includes('scan')) {
                      return { 
                        icon: '🔍', 
                        color: 'bg-blue-100', 
                        textColor: 'text-blue-600', 
                        barColor: 'bg-blue-500',
                        name: 'QR Code'
                      };
                    } else if (name.includes('instagram') || name.includes('ig')) {
                      return { 
                        icon: '📸', 
                        color: 'bg-pink-100', 
                        textColor: 'text-pink-600', 
                        barColor: 'bg-pink-500',
                        name: 'Instagram'
                      };
                    } else if (name.includes('facebook') || name.includes('fb')) {
                      return { 
                        icon: '👍', 
                        color: 'bg-blue-100', 
                        textColor: 'text-blue-600', 
                        barColor: 'bg-blue-500',
                        name: 'Facebook'
                      };
                    } else if (name.includes('telegram')) {
                      return { 
                        icon: '✈️', 
                        color: 'bg-cyan-100', 
                        textColor: 'text-cyan-600', 
                        barColor: 'bg-cyan-500',
                        name: 'Telegram'
                      };
                    } else if (name.includes('tiktok')) {
                      return { 
                        icon: '🎵', 
                        color: 'bg-black', 
                        textColor: 'text-white', 
                        barColor: 'bg-black',
                        name: 'TikTok'
                      };
                    } else {
                      return { 
                        icon: '🌐', 
                        color: 'bg-gray-100', 
                        textColor: 'text-gray-600', 
                        barColor: 'bg-gray-500',
                        name: methodName
                      };
                    }
                  };

                  const platform = getPlatformConfig(method.method);
                  const percentage = metrics.totalEngagements > 0 ? (method.count / metrics.totalEngagements) * 100 : 0;
                  
                  return (
                    <div key={index} className={`flex items-center justify-between p-4 rounded-lg border-2 hover:shadow-md transition-all duration-300 ${
                      platform.name === 'WhatsApp' ? 'bg-green-50 border-green-200 hover:bg-green-100' :
                      platform.name === 'QR Code' ? 'bg-blue-50 border-blue-200 hover:bg-blue-100' :
                      platform.name === 'Instagram' ? 'bg-pink-50 border-pink-200 hover:bg-pink-100' :
                      platform.name === 'Facebook' ? 'bg-blue-50 border-blue-200 hover:bg-blue-100' :
                      platform.name === 'Telegram' ? 'bg-cyan-50 border-cyan-200 hover:bg-cyan-100' :
                      platform.name === 'TikTok' ? 'bg-gray-50 border-gray-200 hover:bg-gray-100' :
                      'bg-gray-50 border-gray-200 hover:bg-gray-100'
                    }`}>
                      <div className="flex items-center space-x-4">
                        <div className={`w-12 h-12 rounded-full flex items-center justify-center text-xl ${
                          platform.name === 'WhatsApp' ? 'bg-green-500 text-white' :
                          platform.name === 'QR Code' ? 'bg-blue-500 text-white' :
                          platform.name === 'Instagram' ? 'bg-pink-500 text-white' :
                          platform.name === 'Facebook' ? 'bg-blue-600 text-white' :
                          platform.name === 'Telegram' ? 'bg-cyan-500 text-white' :
                          platform.name === 'TikTok' ? 'bg-black text-white' :
                          'bg-gray-500 text-white'
                        }`}>
                          {platform.icon}
                        </div>
                        <div>
                          <div className="flex items-center space-x-2">
                            <span className="font-bold text-gray-900 text-lg">{platform.name}</span>
                            <Badge variant="outline" className={`text-xs ${
                              platform.name === 'WhatsApp' ? 'border-green-300 text-green-700' :
                              platform.name === 'QR Code' ? 'border-blue-300 text-blue-700' :
                              platform.name === 'Instagram' ? 'border-pink-300 text-pink-700' :
                              platform.name === 'Facebook' ? 'border-blue-300 text-blue-700' :
                              platform.name === 'Telegram' ? 'border-cyan-300 text-cyan-700' :
                              platform.name === 'TikTok' ? 'border-gray-300 text-gray-700' :
                              'border-gray-300 text-gray-700'
                            }`}>
                              #{index + 1}
                            </Badge>
                          </div>
                          <div className="text-sm text-gray-600 mt-1">{method.method}</div>
                        </div>
                      </div>
                      <div className="flex items-center space-x-4">
                        <div className="w-24 bg-gray-200 rounded-full h-3">
                          <div 
                            className={`h-3 rounded-full transition-all duration-500 ${
                              platform.name === 'WhatsApp' ? 'bg-green-500' :
                              platform.name === 'QR Code' ? 'bg-blue-500' :
                              platform.name === 'Instagram' ? 'bg-pink-500' :
                              platform.name === 'Facebook' ? 'bg-blue-600' :
                              platform.name === 'Telegram' ? 'bg-cyan-500' :
                              platform.name === 'TikTok' ? 'bg-black' :
                              'bg-gray-500'
                            }`}
                            style={{ width: `${Math.max(percentage, 5)}%` }}
                          ></div>
                        </div>
                        <div className="text-right min-w-[80px]">
                          <div className="font-bold text-xl text-gray-900">{method.count}</div>
                          <div className="text-sm text-gray-600">{Math.min(percentage, 100).toFixed(1)}%</div>
                        </div>
                      </div>
                    </div>
                  );
                })
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <Target className="mx-auto h-8 w-8 mb-2" />
                  <p className="text-lg font-medium">No engagement data available</p>
                  <p className="text-sm">Multi-platform analytics will appear here when customers engage</p>
                  <div className="mt-4 flex justify-center space-x-4 text-xs">
                    <span className="bg-gray-100 px-2 py-1 rounded">📱 WhatsApp</span>
                    <span className="bg-gray-100 px-2 py-1 rounded">🔍 QR Code</span>
                    <span className="bg-gray-100 px-2 py-1 rounded">📸 Instagram</span>
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Visitor Segmentation */}
        <Card>
          <CardHeader>
            <CardTitle>Visitor Segmentation</CardTitle>
            <CardDescription>Visitor behavior patterns & categories - Real data</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {metrics.visitorSegmentation && metrics.visitorSegmentation.length > 0 ? (
                metrics.visitorSegmentation.map((segment: any, index: number) => (
                  <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center space-x-3">
                      <Users className="w-5 h-5 text-gray-500" />
                      <span className="font-medium">{segment.type}</span>
                    </div>
                    <div className="text-right">
                      <div className="font-semibold text-gray-900">{segment.count}</div>
                      <div className="text-sm text-gray-500">{Math.min(segment.percentage, 100)}%</div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center py-4 text-gray-500">
                  <Users className="mx-auto h-6 w-6 mb-2" />
                  <p>No visitor segmentation data available</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Recent Engagement Activity */}
      <Card>
        <CardHeader>
          <CardTitle>Recent Engagement Activity</CardTitle>
          <CardDescription>Latest visitor engagement interactions - Real data</CardDescription>
        </CardHeader>
        <CardContent>
          {metrics.recentEngagements && metrics.recentEngagements.length > 0 ? (
            <div className="space-y-3">
              {metrics.recentEngagements.map((engagement: any, index: number) => (
                <div key={index} className="flex items-center justify-between py-3 border-b last:border-b-0">
                  <div className="flex items-center space-x-3">
                    <Target className="w-4 h-4 text-purple-500" />
                    <div>
                      <div className="font-medium text-gray-900">
                        {engagement.engagement_method || 'Engagement Activity'}
                      </div>
                      <div className="text-sm text-gray-500">
                        {engagement.engagement_data?.visitor || 'Anonymous Visitor'} • 
                        {engagement.engagement_type || 'General'}
                      </div>
                    </div>
                  </div>
                  <div className="text-sm text-gray-500">
                    {formatTime(engagement.engagement_timestamp || engagement.created_at)}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              <Target className="mx-auto h-8 w-8 mb-2" />
              <p>No recent engagement activity</p>
              <p className="text-sm">Engagement interactions will appear here</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
